import React, { useState, useEffect, useReducer, Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDebounce } from 'utils/customHooks';
import UserGroupListComponent from './UserGroupListComponent';
import UserGroupListHeaderComponent from './UserGroupListHeaderComponent';
import GroupIcon from '@mui/icons-material/Group';
import { Card, FakeText, IconButton } from '@totalsoft/rocket-ui';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { initialState, reducer } from '../reducers/containerReducer';
import { useQuery } from 'hooks/apollo';
import { USER_GROUPS_QUERY, PAGED_USER_GROUPS_QUERY } from './queries';

const defaultPager = {
  afterId: 1,
  totalCount: 0,
  pageSize: 10,
  direction: 1,
  page: 0,
  sortBy: 'UserGroupId'
};

const UserGroupListContainer = ({ allUserGroupsLoaded, xUserGroups, onXUserGroupsSave, parentLoading }) => {
  const [t] = useTranslation();
  const [pager, setPager] = useState(defaultPager);
  const [localState, dispatch] = useReducer(reducer, initialState);
  const [editFilters, setEditFilters] = useState({});
  const [userGroupsConfig, setUserGroupsConfig] = useState([]);
  const [paged, setPaged] = useState([]);
  const [filters, setFilters] = useState({});
  const debouncedEditFilters = useDebounce(editFilters, 500);

  const { loading: loadingUserGroups } = useQuery(USER_GROUPS_QUERY, {
    onCompleted: data => {
      setUserGroupsConfig(data?.userGroupsConfig);
    }
  });

  const {
    data,
    loading: loadingPagedUserGroups,
    refetch
  } = useQuery(PAGED_USER_GROUPS_QUERY, {
    variables: {
      pager: {
        pageSize: pager.pageSize,
        afterId: pager.afterId,
        sortBy: pager.sortBy,
        direction: pager.direction,
        page: pager.page
      },
      filters
    },
    onCompleted: data => {
      setPaged(data.pagedUserGroupsConfig.values);
      if (data && pager.totalCount !== data?.pagedUserGroupsConfig?.pagination?.totalCount) {
        setPager(currentPager => ({
          ...currentPager,
          totalCount: data.pagedUserGroupsConfig.pagination.totalCount
        }));
      }
    }
  });

  const onChangeFilterValue = (name, value) => {
    setEditFilters({ ...editFilters, [name]: value });
  };

  const onRefresh = () => refetch();

  useEffect(() => {
    if (localState.setAllChanged !== true) return;
    if (localState.setAllCheckbox === true && !loadingUserGroups && userGroupsConfig !== undefined)
      dispatch({ type: 'OnSetAll', checkedValue: true, userGroups: userGroupsConfig });
    if (localState.setAllCheckbox === false) dispatch({ type: 'OnSetAll', checkedValue: false, xUserGroups: xUserGroups });
  }, [localState.setAllCheckbox, localState.setAllChanged, allUserGroupsLoaded, loadingUserGroups, userGroupsConfig, xUserGroups]);

  useEffect(() => {
    if (!parentLoading) {
      dispatch({
        type: 'OnInitState',
        xUserGroups: xUserGroups
      });
    }
  }, [parentLoading, xUserGroups]);

  const onFiltersChanged = useCallback(
    value => {
      setPager(defaultPager);
      setFilters(value);
    },
    [setPager]
  );

  useEffect(() => {
    if (JSON.stringify(debouncedEditFilters) !== JSON.stringify(filters)) onFiltersChanged(debouncedEditFilters);
  }, [debouncedEditFilters, filters, onFiltersChanged, setPager]);

  const handleChangePage = (page, direction) => {
    if (loadingPagedUserGroups) return;
    const nextAfterId = data?.pagedUserGroupsConfig?.pagination?.nextPage?.afterId;
    const prevAfterId = data?.pagedUserGroupsConfig?.pagination?.prevPage?.afterId;
    const afterId = direction ? nextAfterId : prevAfterId;
    setPager(currentPager => ({ ...currentPager, afterId, page }));
  };

  const handleChangeRowsPerPage = pageSize =>
    setPager({
      ...pager,
      pageSize: parseInt(pageSize, defaultPager.pageSize),
      page: defaultPager.page,
      afterId: defaultPager.afterId
    });

  const onClickSave = useCallback(() => {
    dispatch({
      type: 'OnXUserGroupsSave'
    });
    onXUserGroupsSave(localState.selectedUserGroups);
  }, [localState.selectedUserGroups, onXUserGroupsSave]);

  return (
    <Card
      icon={GroupIcon}
      iconColor='primary'
      title={t('UserGroups.Title')}
      actions={
        <IconButton
          type='save'
          tooltip={t('Tooltips.UserGroups.SaveUserGroupsTooltip')}
          color='transparent'
          fontSize='medium'
          onClick={onClickSave}
          disabled={!localState.selectedUserGroupsChanged}
        />
      }
    >
      <Fragment>
        <Prompt when={localState.selectedUserGroupsChanged} message={t('UserGroups.LeavingWithoutSaving')} />
        {loadingPagedUserGroups || parentLoading ? (
          <FakeText lines={10} />
        ) : (
          <Fragment>
            <UserGroupListHeaderComponent
              dispatch={dispatch}
              filters={editFilters}
              setAllCheckbox={localState.setAllCheckbox}
              onChangeFilterValue={onChangeFilterValue}
            />
            <UserGroupListComponent
              userGroups={paged}
              selectedUserGroups={localState.selectedUserGroups}
              dispatch={dispatch}
              pager={pager}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              onRefresh={onRefresh}
              loading={loadingPagedUserGroups}
            />
          </Fragment>
        )}
      </Fragment>
    </Card>
  );
};

UserGroupListContainer.propTypes = {
  allUserGroupsLoaded: PropTypes.bool,
  parentLoading: PropTypes.bool,
  xUserGroups: PropTypes.array,
  onXUserGroupsSave: PropTypes.func
};

export default UserGroupListContainer;
